<template>
  <div
    style="
      background-color: #f8f8f8;
      min-height: 100vh;
      margin: 0;
      padding: 0;
      width: 100%;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
    "
  >
    <TheHeader />
    <div class="content-area">
      <div class="dashboard-container">
        <div v-if="loading.company" class="loading-container">
          <div class="loading-spinner"></div>
          <p v-if="loading.company">Cargando información de la empresa...</p>
        </div>
        <div v-else-if="error" class="error-container">
          <p>{{ error }} <a v-if="isInvalidLinkError" href="/admin" target="_blank"> aquí</a></p>
        </div>
        <div v-else>
          <!-- Tabs Navigation -->
          <TabsNavigation
            :tabs="navigationTabs"
            :active-tab="activeTab"
            @tab-change="handleTabChange"
          />

          <!-- Tab Content -->
          <div v-if="activeTab === 'inicio'">
            <SummaryView :group-id="companyId" @tab-change="handleTabChange" />
          </div>

          <!-- Incorporaciones Tab Content -->
          <div v-else-if="activeTab === 'incorporaciones'">
            <div>
              <IncorporationsList
                :groupId="companyId"
                :show-register-button="true"
                :show-restore-link-button="false"
              />
            </div>
          </div>

          <!-- Coberturas Tab Content -->
          <div v-else-if="activeTab === 'membresias'">
            <MembershipsTableView
              :origen="'dashboard'"
              :loading="loading.employees"
              :error="error"
              :items-per-page="itemsPerPage"
              :current-page="currentPage"
              @update:current-page="currentPage = $event"
              @update:items-per-page="itemsPerPage = $event"
            />
          </div>

          <!-- Facturación Tab Content -->
          <div v-else-if="activeTab === 'facturacion'" class="facturacion-content">
            <InvoiceCollectionsHistory
              :history-data="facturacionHistoryData"
              :is-loading="loading.facturacion"
              :show-title="false"
              :show-edit-button="false"
              :show-concept-column="true"
              :show-payment-date-column="false"
              :show-receipt-column="true"
              :receipt-column-title="'COMPROBANTE DE PAGO'"
              :show-receipt-info-button="true"
              :show-amount-to-pay-column="true"
              :show-prorroga-column="false"
              :is-dashboard-view="true"
              @download-document="handleDownloadDocument"
              @edit-document="handleEditDocument"
              @reload-data="handleReloadFacturacionData"
              @upload-started="handleUploadStarted"
              @upload-finished="handleUploadFinished"
              :origen="'dashboard'"
            />
          </div>

          <!-- Configuración Tab Content -->
          <div v-else-if="activeTab === 'configuracion'">
            <GroupSettings :show-agent-selector="false" ref="settingsRef" />
          </div>
        </div>
      </div>
    </div>

    <!-- Loading for payment proof upload -->
    <CustomLoading :is-visible="isUploadingPaymentProof" text="Subiendo comprobante..." />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import TheHeader from '@/onboarding/components/TheHeader.vue'
import TabsNavigation from '@/components/TabsNavigation.vue'
import MembershipsTableView from '@/components/MembershipsTableView.vue'
import { ApiService } from '@/services/apiService'
import type { CompanyAdministratorEmailsDto, GroupResponseDTO } from 'commons'
import { FileTypes } from 'commons'
import type { HistoryItem } from '@/utils/file-history'
import IncorporationsList from '../components/IncorporationsList.vue'
import SummaryView from '@/onboarding/components/SummaryView.vue'
import InvoiceCollectionsHistory from '@/components/InvoiceCollectionsHistory.vue'
import CustomLoading from '@/components/CustomLoading.vue'
import GroupSettings from '@/components/GroupSettings.vue'
import { decodeJwt } from '@/utils/jwtDecoder'
import { formatMonthYear } from '@/utils/dateParsing'
import { useBulkLoadStore } from '@/stores/bulkLoad'
import { trackGroupEvent } from '@/utils/googleAnalyticsEventsSender'

const route = useRoute()
const router = useRouter()

const company = ref<GroupResponseDTO | null>(null)
const loading = ref({ company: true, employees: true, incorporations: true, facturacion: false })
const error = ref<string | null>(null)
const isUploadingPaymentProof = ref(false)

// Ref for Settings component
const settingsRef = ref()

const currentPage = ref(1)
const itemsPerPage = ref(15)

// Tabs navigation - initialize from URL query parameter
const activeTab = ref((route.query?.tab as string) || 'inicio')

const companyId = computed(() => {
  const groupId = route.params.groupId as string
  const decoded = decodeJwt(groupId)
  if (decoded === null) {
    return null
  }
  const currentTime = Date.now() / 1000
  if (decoded.exp < currentTime) {
    return null
  }
  return decoded.resourceId || ''
})

const tokenKey = computed(() => {
  const groupId = route.params.groupId as string
  return decodeJwt(groupId)?.jti || ''
})

const isInvalidLinkError = ref(false)

const administrators = ref<CompanyAdministratorEmailsDto | null>(null)
const bulkLoadStore = useBulkLoadStore()

watch(companyId, (val) => {
  console.log('watch:', val)
  if (val === null || val === '') {
    error.value = `El Link es inválido o ha caducado. Puede solicitar un nuevo acceso`
    isInvalidLinkError.value = true
  } else {
    isInvalidLinkError.value = false
  }
})

// Watch for URL changes to handle deep linking
watch(
  () => route.query?.tab,
  (newTab) => {
    if (newTab && typeof newTab === 'string') {
      // Validate that the tab exists in our navigation tabs
      const validTabs = navigationTabs.value.map((tab) => tab.key)
      if (validTabs.includes(newTab)) {
        activeTab.value = newTab
        // Load facturación data if switching to that tab
        if (newTab === 'facturacion' && facturacionHistoryData.value.length === 0) {
          fetchFacturacionData()
        }
      } else {
        // Invalid tab, redirect to default
        router.push({
          query: { ...(route.query || {}), tab: 'inicio' },
        })
      }
    }
  },
)
const navigationTabs = ref([
  { key: 'inicio', label: 'Inicio' },
  { key: 'incorporaciones', label: 'Incorporaciones' },
  { key: 'membresias', label: 'Coberturas' },
  { key: 'facturacion', label: 'Facturación' },
  { key: 'configuracion', label: 'Configuración' },
])

const apiService = new ApiService()

const fetchCompany = async () => {
  try {
    loading.value.company = true
    company.value = await apiService.getGroup(companyId.value!)
    trackGroupEvent('acceso_empresa', {
      location: 'Dashboard',
      company_name: company.value?.nombrePublico || '',
    })
  } catch (err) {
    error.value = 'Empresa no encontrada: ' + err
  } finally {
    loading.value.company = false
  }
}

/**
 * Finds a file by type in the status account files array.
 *
 * @param files - Array of status account files
 * @param fileType - The file type to find
 * @returns The file if found, undefined otherwise
 */
const findFileByType = (
  files: Array<{ fileType: FileTypes; fileName?: string; id?: string }>,
  fileType: FileTypes,
) => {
  return files.find((file) => file.fileType === fileType)
}

/**
 * Formats a date (Date object or string) to locale date string.
 *
 * @param date - The date to format (Date, string, or null/undefined)
 * @returns Formatted date string or empty string
 */
const formatDateString = (date: Date | string | null | undefined): string => {
  if (!date) {
    return ''
  }
  const dateObj = typeof date === 'string' ? new Date(date + 'T00:00:00') : date
  return dateObj.toLocaleDateString('es-ES')
}

/**
 * Gets file name or empty string from file object.
 *
 * @param file - The file object
 * @returns File name or empty string
 */
const getFileName = (file: { fileName?: string } | undefined): string => {
  return file?.fileName || ''
}

/**
 * Gets file ID or empty string from file object.
 *
 * @param file - The file object
 * @returns File ID or empty string
 */
const getFileId = (file: { id?: string } | undefined): string => {
  return file?.id || ''
}

/**
 * Extracts file information from status account files.
 *
 * @param files - Array of status account files
 * @returns Object with file information by type
 */
const extractFileInfo = (files: Array<{ fileType: FileTypes; fileName?: string; id?: string }>) => {
  const accountStatementFile = findFileByType(files, FileTypes.ACCOUNT_STATEMENT)
  const preInvoiceFile = findFileByType(files, FileTypes.PRE_INVOICE)
  const invoiceFile = findFileByType(files, FileTypes.INVOICE)
  const paymentProofFile = findFileByType(files, FileTypes.PAYMENT_PROOF)
  const xmlInvoiceFile = findFileByType(files, FileTypes.XML_INVOICE)

  return {
    accountStatement: getFileName(accountStatementFile),
    accountStatementId: getFileId(accountStatementFile),
    preInvoice: getFileName(preInvoiceFile),
    preInvoiceId: getFileId(preInvoiceFile),
    invoice: getFileName(invoiceFile),
    invoiceId: getFileId(invoiceFile),
    receipt: getFileName(paymentProofFile),
    receiptId: getFileId(paymentProofFile),
    xmlInvoice: getFileName(xmlInvoiceFile),
    xmlInvoiceId: getFileId(xmlInvoiceFile),
  }
}

/**
 * Transforms a status account period item to history item format.
 *
 * @param item - The status account period item
 * @returns The transformed history item
 */
const transformStatusAccountItem = (item: {
  id?: string
  month: number
  year: number
  paymentExtensionDate?: Date | string | null
  paymentMadeDate?: Date | string | null
  amountToPay?: number | null
  concept?: string | null
  statusAccountFiles: Array<{ fileType: FileTypes; fileName?: string; id?: string }>
}): HistoryItem => {
  const fileInfo = extractFileInfo(item.statusAccountFiles)

  return {
    id: item.id || '',
    billingMonth: formatMonthYear(item.month, item.year.toString()).toUpperCase(),
    paymentLimitDate: '', // paymentLimitDate moved to company.paymentLimitDay
    paymentExtensionDate: formatDateString(item.paymentExtensionDate),
    accountStatement: fileInfo.accountStatement,
    preInvoice: fileInfo.preInvoice,
    invoice: fileInfo.invoice,
    accountStatementId: fileInfo.accountStatementId,
    preInvoiceId: fileInfo.preInvoiceId,
    invoiceId: fileInfo.invoiceId,
    paymentMadeDate: formatDateString(item.paymentMadeDate),
    amountToPay: item.amountToPay?.toString() || '0',
    concept: item.concept || '',
    receipt: fileInfo.receipt,
    receiptId: fileInfo.receiptId,
    xmlInvoice: fileInfo.xmlInvoice,
    xmlInvoiceId: fileInfo.xmlInvoiceId,
  }
}

const fetchFacturacionData = async () => {
  try {
    loading.value.facturacion = true
    const response = await apiService.getStatusAccountFiles(companyId.value!)

    // Transform API response to match our history data format
    facturacionHistoryData.value = response.map(transformStatusAccountItem)
  } catch (err) {
    console.error('Hubo un error: Error fetching facturación data ', err)
    error.value = 'Error al cargar datos de facturación'
  } finally {
    loading.value.facturacion = false
  }
}

const handleTabChange = (tabKey: string) => {
  trackGroupEvent('active_tab', { dashboard_tab: tabKey })
  activeTab.value = tabKey

  // Update URL with the new tab
  router.push({
    query: { ...(route.query || {}), tab: tabKey },
  })

  // Load facturación data when switching to that tab
  if (tabKey === 'facturacion' && facturacionHistoryData.value.length === 0) {
    fetchFacturacionData()
  }
}

// Facturación history data
const facturacionHistoryData = ref<HistoryItem[]>([])

// Event handlers for facturación
const handleDownloadDocument = (filename: string) => {
  console.log('Downloading document:', filename)
  // Implement download logic here
}

const handleEditDocument = (filename: string) => {
  console.log('Editing document:', filename)
  // Implement edit logic here
}

const handleReloadFacturacionData = async () => {
  console.log('Reloading facturación data...')
  try {
    await fetchFacturacionData()
  } catch (error) {
    console.error('Hubo un error: Error reloading facturación data ', error)
  }
}

const handleUploadStarted = () => {
  isUploadingPaymentProof.value = true
}

const handleUploadFinished = () => {
  isUploadingPaymentProof.value = false
}

onMounted(async () => {
  if (companyId.value === null || companyId.value === '') {
    loading.value.company = false
    loading.value.employees = false
    error.value = 'El Link es inválido o ha caducado. Puede solicitar un nuevo acceso'
    isInvalidLinkError.value = true
    return
  }
  const isValidTokenKey = await apiService.verifyAdminAccessTokenKey(
    tokenKey.value,
    companyId.value!,
  )
  if (!isValidTokenKey.valid) {
    loading.value.company = false
    loading.value.employees = false
    error.value = 'El Link es inválido o ha caducado. Puede solicitar un nuevo acceso'
    isInvalidLinkError.value = true
    return
  }
  Promise.all([fetchCompany(), loadAdministrators()])

  // Load facturación data if the tab is already set in the URL
  if (activeTab.value === 'facturacion') {
    fetchFacturacionData()
  }
})

const loadAdministrators = async () => {
  try {
    administrators.value = await apiService.getCompanyAdministrators(companyId.value!)
    bulkLoadStore.setCompanyAdministrators(administrators.value)
  } catch (error) {
    console.error('Hubo un error: Error loading administrators ', error)
  }
}
</script>

<style scoped>
/* Override any header margins */
:deep(.header) {
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}

.content-area {
  padding: 0 !important;
  margin: 0 !important;
  padding-top: 120px !important;
  width: 100% !important;
  max-width: 100% !important;
  overflow-x: hidden !important;
  overflow-y: visible !important;
  box-sizing: border-box !important;
}

.dashboard-container {
  margin: 0 auto;
  max-width: 100%;
  width: 100%;
  padding: 0 90px;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: visible;
}

/* Responsive padding adjustments for smaller screens */
@media (max-width: 1400px) {
  .dashboard-container {
    padding: 0 80px;
  }
}

@media (max-width: 1280px) {
  .dashboard-container {
    padding: 0 40px;
  }
}

@media (max-width: 768px) {
  .dashboard-container {
    padding: 0 20px;
  }
}

h2 {
  color: #333;
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #003896;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-container {
  text-align: center;
  padding: 2rem;
  color: #d32f2f;
}

/* Not implemented container styles */
.not-implemented-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  min-height: 400px;
}

.not-implemented-icon {
  margin-bottom: 1.5rem;
}

.not-implemented-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
  font-family: 'Montserrat', sans-serif;
}

.not-implemented-message {
  font-size: 1.1rem;
  color: #666;
  line-height: 1.5;
  font-family: 'Montserrat', sans-serif;
}

/* Facturación content spacing */
.facturacion-content {
  margin-top: 24px;
}
</style>
